import React, { useEffect, useState } from 'react';
import { ArrowRight, Pause, Play, RotateCcw, PhoneIncoming, ClipboardCheck, CalendarCheck, MessageSquareText, BellRing, Target, Workflow, ShieldCheck } from 'lucide-react';
import { scrollToId, applyNow } from './homeData';
import { SCENARIOS, stageLabels } from './heroScenarios';

const DEMO_METRICS = [['Inquiries Logged', '24'], ['Followed Up', '24'], ['Appointment Requests', '9'], ['Team Alerts', '6']];

const PerformanceStrip = () => (
  <div className="yv-hd-perf" data-testid="hero-demo-performance">
    <div className="yv-hd-pane-head yv-mono">Performance Reporting · Demo Data</div>
    <dl>{DEMO_METRICS.map(([k, v]) => <div key={k}><dt>{k}</dt><dd className="yv-mono">{v}</dd></div>)}</dl>
  </div>
);

const STAGE_ICONS = [PhoneIncoming, ClipboardCheck, CalendarCheck, MessageSquareText, BellRing];
const STAGE_IDS = ['call', 'details', 'scheduled', 'confirmation', 'team'];
const COUNT = STAGE_IDS.length;

const useSequence = () => {
  const [playing, setPlaying] = useState(false);
  const [i, setI] = useState(COUNT - 1);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!playing) return;
    const t = setTimeout(() => {
      if (i >= COUNT - 1) { setPlaying(false); return; }
      setI(i + 1);
    }, i === 0 ? 1300 : 1500);
    return () => clearTimeout(t);
  }, [playing, i, n]);
  const toggle = () => { if (playing) { setPlaying(false); setI(COUNT - 1); } else { if (i >= COUNT - 1) setI(0); setPlaying(true); } };
  const replay = () => { setI(0); setPlaying(true); setN((v) => v + 1); };
  const reset = () => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setPlaying(!reduce);
    setI(reduce ? COUNT - 1 : 0);
    setN((v) => v + 1);
  };
  return { i, playing, toggle, replay, reset };
};

const SchedChip = ({ s }) => (
  <div className={`yv-hd-sched ${s.confirmed ? '' : 'is-request'}`} data-testid="hero-demo-appt">
    <CalendarCheck className="w-3.5 h-3.5" aria-hidden="true" />
    <span>{s.confirmed ? `Confirmed By ${s.approver}: ${s.time}` : `Requested: ${s.time} · Awaiting Team Approval`}</span>
  </div>
);

const CallPane = ({ i, s }) => (
  <div className="yv-hd-pane yv-keep-case" aria-label="Sample call and messages">
    <div className="yv-hd-pane-head yv-mono">Call &amp; Messages</div>
    <div className={`yv-hd-call ${i === 0 ? 'ringing' : ''}`}>
      <PhoneIncoming className="w-3.5 h-3.5" aria-hidden="true" /> {i === 0 ? `Incoming Call · ${s.caller}` : 'Call Answered · Details Captured'}
    </div>
    {i >= 2 && <SchedChip s={s} />}
    {i >= 3 && <div className="yv-hd-bubble out" data-testid="hero-demo-message">{s.message}</div>}
  </div>
);

const RequestPane = ({ i, s }) => {
  const rows = [
    ['Industry', s.tab, 0], ['Service', s.title, 1], [s.fields[0][0], s.fields[0][1], 1], [s.fields[1][0], s.fields[1][1], 1],
    [s.confirmed ? 'Appointment' : 'Requested Time', s.time, 2],
    ['Status', s.confirmed ? `Confirmed · Approved By ${s.approver}` : 'Requested · Awaiting Approval', 2],
  ];
  return (
    <div className="yv-hd-pane" aria-label="Sample service request">
      <div className="yv-hd-pane-head yv-mono">Service Request</div>
      <dl className="yv-hd-record" data-testid="hero-demo-record">
        {rows.map(([k, v, at]) => (
          <div key={k} className={i >= at ? 'on' : ''}>
            <dt>{k}</dt>
            <dd className="yv-keep-case">{i >= at ? v : '—'}</dd>
          </div>
        ))}
      </dl>
      {i >= 4 && <div className="yv-hd-alert" data-testid="hero-demo-alert"><BellRing className="w-3.5 h-3.5" aria-hidden="true" /> Team Notified: {s.alert}</div>}
    </div>
  );
};

const IndustryTabs = ({ sel, onPick }) => (
  <div className="yv-hd-tabs-wrap">
    <div className="yv-hd-tabs-label yv-mono" id="hd-tabs-label">Choose Your Industry</div>
    <div className="yv-hd-tabs" role="tablist" aria-labelledby="hd-tabs-label">
      {SCENARIOS.map((x) => (
        <button key={x.id} role="tab" aria-selected={sel.id === x.id} onClick={() => onPick(x)} className={sel.id === x.id ? 'on' : ''} data-testid={`hero-industry-${x.id}`}>{x.tab}</button>
      ))}
    </div>
  </div>
);

const HeroDemo = () => {
  const [sel, setSel] = useState(SCENARIOS[0]);
  const { i, playing, toggle, replay, reset } = useSequence();
  const labels = stageLabels(sel);
  const pick = (x) => { if (x.id === sel.id) return; setSel(x); reset(); };
  return (
    <div className="yv-hd" id="hero-demo" data-testid="hero-demo">
      <div className="yv-hd-top">
        <span className="yv-tag yv-tag-light" data-testid="hero-demo-label">Operations Dashboard · Demo Data</span>
        <div className="yv-hd-ctrl">
          <button className="yv-hd-btn" onClick={toggle} aria-label={playing ? 'Pause sample workflow' : 'Play sample workflow'} data-testid="hero-demo-toggle">
            {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}{playing ? 'Pause' : 'Play'}
          </button>
          <button className="yv-hd-btn" onClick={replay} aria-label="Replay sample workflow" data-testid="hero-demo-replay"><RotateCcw className="w-3.5 h-3.5" />Replay</button>
        </div>
      </div>
      <IndustryTabs sel={sel} onPick={pick} />
      <div className="yv-hd-scenario" data-testid="hero-demo-scenario">{sel.tab} · {sel.title}</div>
      <ol className="yv-hd-rail" aria-label="Sample workflow steps">
        {labels.map((label, k) => {
          const Icon = STAGE_ICONS[k];
          return (
            <li key={STAGE_IDS[k]} className={`${k <= i ? 'done' : ''} ${k === i ? 'current' : ''}`} aria-current={k === i ? 'step' : undefined} data-testid={`hero-stage-${STAGE_IDS[k]}`}>
              <span className="yv-hd-dot"><Icon className="w-3.5 h-3.5" aria-hidden="true" /></span>
              <span className="yv-hd-rail-label">{label}</span>
            </li>
          );
        })}
      </ol>
      <div className="yv-hd-body" aria-live="polite">
        <CallPane i={i} s={sel} />
        <RequestPane i={i} s={sel} />
      </div>
      <PerformanceStrip />
      <p className="yv-hd-foot">Illustrative Activity · Not A Real Customer. Demonstration Data Only. Press Replay To See The Workflow.</p>
    </div>
  );
};

const CAPS = [
  { id: 'capture', icon: Target, t: 'Capture Every Opportunity', d: 'Inquiry Recovery & Follow-Up' },
  { id: 'connect', icon: Workflow, t: 'Connect Your Operations', d: 'CRM & Scheduling Integration' },
  { id: 'running', icon: ShieldCheck, t: 'Keep Systems Running', d: 'Managed Automation & Support' },
];

const HeroCopy = () => (
  <div className="yv-hero-copy">
    <div className="yv-hero-copy-top">
    <span className="yv-hero-badge" data-testid="hero-location-label">Atlanta-Based. Serving Businesses Nationwide.</span>
    <h1 className="yv-hero-title" data-testid="hero-title">
      Your Business Moves Faster When <span className="yv-hero-accent">Every Conversation Connects.</span>
    </h1>
    <p className="yv-hero-sub" data-testid="hero-subtitle">
      YOVAI Designs, Integrates, And Manages Business Communication Infrastructure That Connects Customer Inquiries, CRM Systems, Scheduling, Follow-Up, And Revenue Operations.
    </p>
    <div className="yv-hero-actions">
      <button onClick={() => applyNow()} className="yv-btn yv-btn-primary" data-testid="hero-apply-btn">
        Apply For A Consultation <ArrowRight className="w-4 h-4" aria-hidden="true" />
      </button>
      <button onClick={() => scrollToId('solutions')} className="yv-btn yv-btn-ghost" data-testid="hero-solutions-btn">
        Explore Our Solutions
      </button>
    </div>
    </div>
    <div className="yv-hero-copy-bottom">
    <ul className="yv-hcaps" data-testid="hero-capabilities">
      {CAPS.map((c, i) => {
        const Icon = c.icon;
        return (
          <li key={c.id} style={{ animationDelay: `${300 + i * 120}ms` }} data-testid={`hero-cap-${c.id}`}>
            <Icon className="w-5 h-5" aria-hidden="true" />
            <div><b>{c.t}</b><span>{c.d}</span></div>
          </li>
        );
      })}
    </ul>
    <p className="yv-hero-trust" data-testid="hero-trust-line">Designed Around Your Business. Implemented With Precision. Managed For Long-Term Reliability.</p>
    </div>
  </div>
);

const HeroBackdrop = () => (
  <div className="yv-hbg" aria-hidden="true">
    <svg viewBox="0 0 1440 820" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="yv-trail" x1="0" x2="1"><stop offset="0" stopColor="#1d7bff" stopOpacity="0" /><stop offset="0.6" stopColor="#38bdf8" stopOpacity="0.55" /><stop offset="1" stopColor="#38bdf8" stopOpacity="0.1" /></linearGradient>
      </defs>
      {['M-40 140 C 320 120, 560 300, 980 330', 'M-40 420 C 300 400, 620 440, 980 400', 'M-40 700 C 360 690, 600 520, 980 470', 'M200 -20 C 360 200, 700 260, 980 360', 'M260 860 C 420 640, 760 560, 980 440'].map((d, i) => (
        <g key={d}>
          <path d={d} className="yv-hbg-line" />
          <path d={d} className="yv-hbg-flow" style={{ animationDelay: `${i * 1.6}s` }} stroke="url(#yv-trail)" />
        </g>
      ))}
      {[[180, 128], [420, 230], [640, 300], [300, 410], [700, 430], [380, 600], [690, 520], [520, 120]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r="2.5" className="yv-hbg-node" style={{ animationDelay: `${i * 0.7}s` }} />)}
    </svg>
  </div>
);

export const HomeHero = () => (
  <section id="top" className="yv-hero yv-dark yv-grid-dark" data-testid="home-hero">
    <HeroBackdrop />
    <div className="yv-container yv-hero-grid">
      <HeroCopy />
      <HeroDemo />
    </div>
  </section>
);
